import { useState } from 'react';
import { averageSteps, activityLevelFromSteps, HealthError } from '../lib/health';
import { formatRelative } from '../lib/dates';
import { useApp } from '../store/AppStore';
import { Sheet } from './Sheet';
import { IconInfo } from './Icons';

const ENDPOINT_HINT = 'https://fitnessa-aplik-cija.pages.dev/api/health';

export function HealthSync() {
  const { state, setHealth, syncHealth, setProfile } = useApp();
  const health = state.settings.health;

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guide, setGuide] = useState(false);

  const avg = averageSteps(state.healthDays);
  const suggested = avg ? activityLevelFromSteps(avg) : undefined;
  const mismatch = suggested && suggested !== state.profile.activityLevel;

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { days, cardioAdded } = await syncHealth();
      setResult(
        `Synced ${days} day${days === 1 ? '' : 's'}` +
          (cardioAdded > 0
            ? ` and added ${cardioAdded} cardio session${cardioAdded === 1 ? '' : 's'}.`
            : '.'),
      );
    } catch (err) {
      setError(
        err instanceof HealthError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Sync failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="card-title" style={{ margin: 0 }}>
          Apple Health
        </span>
        <button className="btn ghost sm" onClick={() => setGuide(true)}>
          Setup guide
        </button>
      </div>

      <div className="toggle-row">
        <div className="grow">
          <div>Pull in steps and workouts</div>
          <div className="tiny faint">
            {health.lastSyncedAt
              ? `Last synced ${formatRelative(health.lastSyncedAt.slice(0, 10))}`
              : 'Not connected yet'}
          </div>
        </div>
        <input
          type="checkbox"
          checked={health.enabled}
          onChange={(e) => setHealth({ enabled: e.target.checked })}
          aria-label="Enable Apple Health sync"
        />
      </div>

      {health.enabled && (
        <div className="stack" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="h-token">Token</label>
            <input
              id="h-token"
              type="password"
              value={health.token}
              placeholder="Paste the same value as HEALTH_TOKEN"
              onChange={(e) => setHealth({ token: e.target.value })}
            />
            <div className="hint">
              Must match the <code>HEALTH_TOKEN</code> environment variable on your
              Cloudflare Pages project, and the token in your Shortcut.
            </div>
          </div>

          <div className="field">
            <label htmlFor="h-endpoint">Endpoint</label>
            <input
              id="h-endpoint"
              value={health.endpoint}
              placeholder={ENDPOINT_HINT}
              onChange={(e) => setHealth({ endpoint: e.target.value })}
            />
            <div className="hint">Leave as <code>/api/health</code> unless you moved it.</div>
          </div>

          <div className="toggle-row">
            <div className="grow">
              <div>Auto-log cardio</div>
              <div className="tiny faint">
                Turn imported treadmill, walk, run and bike sessions into cardio
                entries. Strength workouts are ignored — you already log those set
                by set.
              </div>
            </div>
            <input
              type="checkbox"
              checked={health.autoImportWorkouts}
              onChange={(e) => setHealth({ autoImportWorkouts: e.target.checked })}
              aria-label="Auto-log cardio from Health workouts"
            />
          </div>

          <button className="btn block" onClick={run} disabled={busy || !health.token.trim()}>
            {busy ? 'Syncing…' : 'Sync now'}
          </button>

          {result && <div className="note small">{result}</div>}
          {error && (
            <div className="warning">
              <IconInfo size={15} />
              <span>{error}</span>
            </div>
          )}

          {avg !== undefined && (
            <div className="grid-2">
              <div className="stat">
                <div className="label">Avg daily steps</div>
                <div className="value">{avg.toLocaleString('en-GB')}</div>
              </div>
              <div className="stat">
                <div className="label">Days of data</div>
                <div className="value">{state.healthDays.length}</div>
              </div>
            </div>
          )}

          {mismatch && (
            <div className="note small">
              Your real step count suggests your activity level is{' '}
              <strong>{suggested}</strong>, not {state.profile.activityLevel}. That
              changes your calorie target.
              <button
                className="btn sm block"
                style={{ marginTop: 9 }}
                onClick={() => setProfile({ activityLevel: suggested })}
              >
                Use {suggested} instead
              </button>
            </div>
          )}
        </div>
      )}

      <SetupGuide open={guide} onClose={() => setGuide(false)} />
    </div>
  );
}

function SetupGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} title="Connecting Apple Health" onClose={onClose}>
      <p className="small muted">
        Apple doesn't let a website read Health directly — there's no web API for
        it, so this can't be a simple "connect" button. The way round it is the
        Shortcuts app, which <em>can</em> read Health and send it here on a
        schedule. Takes about fifteen minutes once.
      </p>

      <div className="note small" style={{ margin: '12px 0' }}>
        Worth doing first: open the Technogym / MyFitness app and check that Apple
        Health sync is switched on. If it is, your treadmill sessions already land
        in Health, and they'll flow through here automatically.
      </div>

      <div className="card-title" style={{ marginTop: 16 }}>1 · Cloudflare (once)</div>
      <ol className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
        <li style={{ marginBottom: 6 }}>
          Open your Pages project → <strong>Settings → Bindings</strong> → add a
          <strong> D1 database</strong> binding. Variable name{' '}
          <code>HEALTH_DB</code>, database <code>iron-ledger-health</code>.
        </li>
        <li style={{ marginBottom: 6 }}>
          <strong>Settings → Environment variables</strong> → add{' '}
          <code>HEALTH_TOKEN</code>. Make it a long random string — treat it like a
          password, since anyone with it can read your activity.
        </li>
        <li style={{ marginBottom: 6 }}>
          Redeploy (Deployments → ⋯ → Retry deployment) so the bindings take effect.
        </li>
        <li>Paste the same token into the Token field on this screen.</li>
      </ol>

      <div className="card-title" style={{ marginTop: 18 }}>2 · The Shortcut</div>
      <p className="small muted">
        Shortcuts app → <strong>+</strong> → add these actions in order. Action names
        shift slightly between iOS versions, so search for the keyword rather than
        expecting an exact match.
      </p>
      <ol className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
        <li style={{ marginBottom: 6 }}>
          <strong>Find Health Samples</strong> — Type <em>Steps</em>, filter{' '}
          <em>Start Date is Today</em>.
        </li>
        <li style={{ marginBottom: 6 }}>
          <strong>Calculate Statistics</strong> — <em>Sum</em> of the samples. Then{' '}
          <strong>Set Variable</strong> → name it <code>Steps</code>.
        </li>
        <li style={{ marginBottom: 6 }}>
          Repeat both for <em>Active Energy</em> → variable <code>Energy</code>.
        </li>
        <li style={{ marginBottom: 6 }}>
          <strong>Text</strong> action, with this exactly — insert the variables
          where shown:
          <pre className="codeblock">{`{"days":[{"date":"«Current Date, formatted yyyy-MM-dd»",
"steps":«Steps»,"activeEnergyKcal":«Energy»}]}`}</pre>
        </li>
        <li style={{ marginBottom: 6 }}>
          <strong>Get Contents of URL</strong> — URL{' '}
          <code>{ENDPOINT_HINT}</code>, Method <strong>POST</strong>, Headers:{' '}
          <code>Authorization</code> = <code>Bearer YOUR_TOKEN</code>, Request Body{' '}
          <strong>File</strong> → the Text from the previous step.
        </li>
        <li>
          Run it once. You should get <code>{'{"ok":true…}'}</code> back. Then tap{' '}
          <strong>Sync now</strong> here.
        </li>
      </ol>

      <div className="card-title" style={{ marginTop: 18 }}>3 · Make it automatic</div>
      <p className="small muted">
        Shortcuts → <strong>Automation</strong> → <strong>+</strong> → Time of Day →
        pick something late like 23:00, daily → run your shortcut. Turn{' '}
        <strong>Run Immediately</strong> on and <strong>Notify When Run</strong> off,
        so it happens silently.
      </p>

      <div className="card-title" style={{ marginTop: 18 }}>Optional · workouts</div>
      <p className="small muted">
        To pull individual sessions, add a second <strong>Find Health Samples</strong>{' '}
        for <em>Workouts</em> in the last 7 days, then <strong>Repeat with Each</strong>{' '}
        building a <code>workouts</code> array alongside <code>days</code>. Each entry
        needs a unique <code>id</code> (use the sample's start date and time) plus{' '}
        <code>date</code>, and optionally <code>activity</code>, <code>minutes</code>,{' '}
        <code>distanceKm</code>, <code>energyKcal</code>, <code>avgHr</code> and{' '}
        <code>source</code>. Re-sending the same id updates rather than duplicates, so
        overlapping runs are safe.
      </p>

      <div className="warning" style={{ marginTop: 16 }}>
        <span>
          Once this is on, your step counts and workout summaries live on Cloudflare
          rather than only on your phone. No names, no photos — just activity
          numbers, behind your token. Everything else in the app still stays on the
          device.
        </span>
      </div>
    </Sheet>
  );
}
